import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

import { IBaseProps, Nullable, SetState } from '@rbi-ctg/frontend';
import { useIsModifiedTakeout } from 'hooks/use-is-modified-takeout';
import { useMParticleContext } from 'state/mParticle';
import { serializePickupMode, serializeServiceMode } from 'state/mParticle/utils';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import noop from 'utils/noop';

import { ServiceMode } from './types';

export { ServiceMode };

const preloadedSvcMode = () => {
  const preloaded = LocalStorage.getItem(StorageKeys.SERVICE_MODE);
  return preloaded ? preloaded.serviceMode : null;
};

const persistServiceMode = (serviceMode: Nullable<ServiceMode>) => {
  LocalStorage.setItem(StorageKeys.SERVICE_MODE, { serviceMode });
};

export interface IServiceModeCtx {
  serviceMode: Nullable<ServiceMode>;
  setServiceMode: SetState<ServiceMode | null>;
  isDelivery: boolean;
  isTakeout: boolean;
  isEatIn: boolean;
  isCurbside: boolean;
}

export const ServiceModeContext = createContext<IServiceModeCtx>({
  serviceMode: null,
  setServiceMode: noop,
  isDelivery: false,
  isTakeout: false,
  isEatIn: false,
  isCurbside: false
});

export const useServiceModeContext = () => useContext(ServiceModeContext);

export const ServiceModeProvider = ({ children }: IBaseProps) => {
  const preloaded = useMemo(() => preloadedSvcMode(), []);
  const [serviceMode, setServiceMode] = useState(preloaded);
  const isModifiedTakeout = useIsModifiedTakeout();

  const isDelivery = serviceMode === ServiceMode.DELIVERY;
  const isTakeout = serviceMode === ServiceMode.TAKEOUT && !isModifiedTakeout;
  const isEatIn = serviceMode === ServiceMode.EAT_IN;
  const isCurbside = serviceMode === ServiceMode.CURBSIDE;

  const { updateUniversalAttributes } = useMParticleContext();

  useEffect(() => {
    persistServiceMode(serviceMode);
    // Updates service/pickup modes for all mParticle events/page views
    updateUniversalAttributes({
      'Service Mode': serializeServiceMode(serviceMode),
      'Pickup Mode': serializePickupMode(serviceMode)
    });
  }, [serviceMode, updateUniversalAttributes]);

  return (
    <ServiceModeContext.Provider
      value={{ serviceMode, setServiceMode, isDelivery, isTakeout, isEatIn, isCurbside }}
    >
      {children}
    </ServiceModeContext.Provider>
  );
};
